import React, { useState } from "react";
import {
  Loader2,
  Trash2,
  ArrowLeft,
  Upload,
  Clock,
  ChevronRight,
  Pencil,
  Save,
  X,
  File,
  Eye,
  Tag,
} from "lucide-react";
import { SkeletonList } from "../../components/Skeleton";
import { FormField, FormInput, FormTextarea } from "../../components/Modal";
import { RBACAction } from "../../components/RBACButton";
import { API_URL } from "../../../constants";
import {
  downloadSupportingEvidenceFile,
  previewSupportingEvidenceFile,
  type SupportingEvidenceResponse,
} from "../../../api/frameworksApi";
import { PreviewModal } from "./PreviewModal";
import { StatCard } from "./StatCard";
import { formatDate, formatFileSize } from "./utils";

/* ── Evidence Detail Component ── */
export function EvidenceDetail({
  evidence,
  loading,
  editMode,
  editTitle,
  editDescription,
  editTags,
  savingEdit,
  onBack,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onEditTitleChange,
  onEditDescriptionChange,
  onEditTagsChange,
  onUpload,
  onDelete,
}: {
  evidence: SupportingEvidenceResponse;
  loading: boolean;
  editMode: boolean;
  editTitle: string;
  editDescription: string;
  editTags: string;
  savingEdit: boolean;
  onBack: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditTitleChange: (v: string) => void;
  onEditDescriptionChange: (v: string) => void;
  onEditTagsChange: (v: string) => void;
  onUpload: () => void;
  onDelete: () => void;
}) {
  const [showPreview, setShowPreview] = useState(false);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          Content Registry
        </button>
        <ChevronRight className="size-3.5 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Supporting Evidence</span>
        <ChevronRight className="size-3.5 text-muted-foreground" />
        <span className="text-sm font-medium">{evidence.title}</span>
      </div>

      {/* Title + actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>{evidence.title}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Supporting Evidence</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          {!editMode && (
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
            >
              <Pencil className="size-4" /> Edit
            </button>
          )}
          {editMode && (
            <>
              <button
                onClick={onCancelEdit}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
              >
                <X className="size-4" /> Cancel
              </button>
              <button
                onClick={onSaveEdit}
                disabled={savingEdit}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
              >
                {savingEdit ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}{" "}
                Save
              </button>
            </>
          )}
          <RBACAction module="evidence" action="delete">
            <button
              onClick={onDelete}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors shadow-md shadow-red-500/20"
            >
              <Trash2 className="size-4" /> Delete
            </button>
          </RBACAction>
        </div>
      </div>

      {loading ? (
        <SkeletonList rows={2} />
      ) : (
        <>
          {/* Overview */}
          <div className="rounded-2xl border border-border bg-gradient-to-br from-card via-card to-indigo-50/30 dark:to-indigo-500/[0.03] overflow-hidden shadow-sm">
            <div className="p-6">
              {editMode ? (
                <div className="grid grid-cols-1 gap-5">
                  <FormField label="Title" required>
                    <FormInput
                      value={editTitle}
                      onChange={onEditTitleChange}
                      placeholder="Evidence title"
                    />
                  </FormField>
                  <FormField label="Description">
                    <FormTextarea
                      value={editDescription}
                      onChange={onEditDescriptionChange}
                      placeholder="Description"
                      rows={3}
                    />
                  </FormField>
                  <FormField label="Tags" hint="Comma-separated">
                    <FormInput
                      value={editTags}
                      onChange={onEditTagsChange}
                      placeholder="audit, soc2, 2024"
                    />
                  </FormField>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Tags */}
                  {evidence.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {evidence.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-xs font-medium text-indigo-600 dark:text-indigo-400 ring-1 ring-indigo-200/60 dark:ring-indigo-500/20"
                        >
                          <Tag className="size-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Description */}
                  {evidence.description && (
                    <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
                      {evidence.description}
                    </p>
                  )}

                  {/* Stat cards row — matches Governance Document detail's card design */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatCard
                      icon={<Clock className="size-4" />}
                      label="Created Date"
                      value={formatDate(evidence.createdAt)}
                      color="slate"
                    />
                    <StatCard
                      icon={<Clock className="size-4" />}
                      label="Last Updated Date"
                      value={formatDate(evidence.updatedAt)}
                      color="slate"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* File */}
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border bg-[#f5f7fb] dark:bg-white/[0.02]">
              <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                <File className="size-4 text-indigo-500 dark:text-indigo-400" />
                File
              </h3>
              <div className="flex items-center gap-2">
                {evidence.hasFile ? (
                  <button
                    onClick={() => setShowPreview(true)}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-border hover:bg-accent transition-colors text-foreground"
                    title="View file"
                  >
                    <Eye className="size-3" /> View
                  </button>
                ) : (
                  <button
                    onClick={onUpload}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-border hover:bg-accent transition-colors text-foreground"
                  >
                    <Upload className="size-3" /> Upload
                  </button>
                )}
              </div>
            </div>
            <div className="p-4">
              {evidence.hasFile ? (
                <div className="flex items-center gap-4">
                  <File className="size-10 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {evidence.fileName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {evidence.contentType} ·{" "}
                      {formatFileSize(evidence.fileSize)}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No file uploaded yet</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Preview modal */}
      {showPreview && evidence.hasFile && (
        <PreviewModal
          url={previewSupportingEvidenceFile(evidence.id)}
          fileName={evidence.fileName}
          downloadUrl={downloadSupportingEvidenceFile(evidence.id)}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}
